import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { GrowthMarketingService, GrowthSocialAccount, GrowthSocialListeningItem, GrowthSocialPost } from './growth-marketing.service';
import { GrowthAdminService, GrowthConnectorDto } from './growth-admin.service';
import { buildGrowthSocialNetworkOptions } from './growth-channel.constants';
import { combineUtcDateAndTime } from './growth-datetime.util';

@Component({
  standalone: true,
  selector: 'ain-growth-social-posts',
  providers: [MessageService],
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    GrowthContentAssetSelectorComponent,
    JalaliDateInputComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TabsModule,
    ToastModule,
    AinDatePipe
  ],
  template: `
    <p-toast />
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.social.posts.title')" [description]="t('growth.social.posts.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.social.posts.help.page.title'"
        [descriptionKey]="'growth.social.posts.help.page.description'"
        [bulletKeys]="[
          'growth.social.posts.help.page.bullets.approval',
          'growth.social.posts.help.page.bullets.sandbox',
          'growth.social.posts.help.page.bullets.listening'
        ]" />

      <p-tabs value="accounts">
        <p-tablist>
          <p-tab value="accounts">{{ t('growth.social.posts.tabs.accounts') }}</p-tab>
          <p-tab value="posts">{{ t('growth.social.posts.tabs.posts') }}</p-tab>
          <p-tab value="listening">{{ t('growth.social.posts.tabs.listening') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="accounts">
            <section class="card">
              <div class="toolbar">
                <button pButton type="button" [label]="t('growth.social.posts.actions.newAccount')" (click)="openNewAccountDialog()"></button>
              </div>
              <p-table [value]="accounts()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.social.posts.fields.network') }}</th>
                    <th>{{ t('growth.social.posts.fields.displayName') }}</th>
                    <th>{{ t('growth.social.posts.fields.chatId') }}</th>
                    <th>{{ t('growth.social.posts.fields.status') }}</th>
                    <th>{{ t('growth.social.posts.fields.sandboxMode') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.networkKey }}</td>
                    <td>{{ row.displayName }}</td>
                    <td>{{ row.externalAccountRef || '—' }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.sandboxMode }}</td>
                    <td class="actions">
                      <button
                        pButton
                        type="button"
                        size="small"
                        [label]="t('growth.social.posts.actions.editAccount')"
                        (click)="openEditAccountDialog(row)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>
          <p-tabpanel value="posts">
            <section class="card">
              <div class="toolbar">
                <button pButton type="button" [label]="t('growth.social.posts.actions.newPost')" (click)="openPostDialog()"></button>
              </div>
              @if (loading()) {
                <ain-state type="loading" [title]="t('common.loading')" />
              } @else {
                <p-table [value]="posts()">
                  <ng-template pTemplate="header"><tr><th>Network</th><th>Status</th><th>Scheduled</th><th>Actions</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.networkKey }}</td>
                      <td>{{ row.status }}</td>
                      <td>{{ row.scheduledAtUtc ? (row.scheduledAtUtc | ainDate:'datetime') : '-' }}</td>
                      <td class="actions">
                        <button
                          pButton
                          type="button"
                          size="small"
                          [disabled]="row.status !== 'Draft' && row.status !== 'InReview'"
                          [label]="t('growth.social.posts.actions.approve')"
                          (click)="approvePost(row)"></button>
                        <button
                          pButton
                          type="button"
                          size="small"
                          [disabled]="row.status !== 'Approved'"
                          [label]="t('growth.social.posts.actions.schedule')"
                          (click)="schedulePost(row)"></button>
                        <button
                          pButton
                          type="button"
                          size="small"
                          [disabled]="row.status !== 'Approved' && row.status !== 'Scheduled'"
                          [label]="t('growth.social.posts.actions.publish')"
                          (click)="publishPost(row)"></button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </section>
          </p-tabpanel>
          <p-tabpanel value="listening">
            <section class="card">
              <div class="toolbar">
                <button pButton type="button" [label]="t('growth.social.posts.actions.ingestListening')" (click)="ingestListening()"></button>
              </div>
              <p-table [value]="listening()">
                <ng-template pTemplate="header"><tr><th>Network</th><th>ExternalRef</th><th>Sentiment</th><th>Status</th></tr></ng-template>
                <ng-template pTemplate="body" let-row><tr><td>{{ row.networkKey }}</td><td>{{ row.externalRef }}</td><td>{{ row.sentimentScore ?? '-' }}</td><td>{{ row.status }}</td></tr></ng-template>
              </p-table>
            </section>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <p-dialog
        [header]="editingAccountId() ? t('growth.social.posts.dialogs.editAccount') : t('growth.social.posts.dialogs.account')"
        [visible]="accountDialogVisible()"
        (visibleChange)="accountDialogVisible.set($event)"
        [modal]="true">
        <div class="grid">
          <ain-form-field [label]="t('growth.social.posts.fields.network')">
            <p-select
              [options]="networkOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="accountModel.networkKey"
              [disabled]="!!editingAccountId()"
              appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.connector')">
            <p-select
              [options]="activeConnectorOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="accountModel.connectorKey"
              [showClear]="true"
              appendTo="body"
              [placeholder]="t('growth.social.posts.fields.connectorPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.displayName')"><input pInputText [(ngModel)]="accountModel.displayName" /></ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.chatId')">
            <input pInputText [(ngModel)]="accountModel.externalAccountRef" [placeholder]="t('growth.social.posts.fields.chatIdPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.botToken')">
            <input
              pInputText
              type="password"
              autocomplete="off"
              [(ngModel)]="accountModel.botToken"
              [placeholder]="editingAccountId()
                ? t('growth.social.posts.fields.botTokenKeepPlaceholder')
                : t('growth.social.posts.fields.botTokenPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.status')">
            <p-select [options]="accountStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="accountModel.status" appendTo="body" />
          </ain-form-field>
          <label class="checkbox-field">
            <p-checkbox [(ngModel)]="accountModel.sandboxMode" [binary]="true" inputId="social-account-sandbox" />
            <span>{{ t('growth.social.posts.fields.sandboxMode') }}</span>
          </label>
          @if (editingAccountId() && accountModel.hasBotCredential) {
            <p class="hint">{{ t('growth.social.posts.hints.tokenConfigured') }}</p>
          }
        </div>
        <ain-info-hint
          [titleKey]="'growth.social.posts.help.accountProvider.title'"
          [descriptionKey]="'growth.social.posts.help.accountProvider.description'"
          [bulletKeys]="[
            'growth.social.posts.help.accountProvider.bullets.messenger',
            'growth.social.posts.help.accountProvider.bullets.token'
          ]" />
        <ng-template pTemplate="footer">
          <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="accountDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="saveAccount()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('growth.social.posts.dialogs.post')" [visible]="postDialogVisible()" (visibleChange)="postDialogVisible.set($event)" [modal]="true">
        <div class="grid">
          <ain-form-field [label]="t('growth.social.posts.fields.contentAsset')">
            <ain-growth-content-asset-selector [(ngModel)]="postModel.contentAssetId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.socialAccount')">
            <p-select [options]="socialAccountOptions()" optionLabel="label" optionValue="value" [(ngModel)]="postModel.socialAccountId" [showClear]="true" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.network')">
            <p-select [options]="networkOptions" optionLabel="label" optionValue="value" [(ngModel)]="postModel.networkKey" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.scheduleDate')"><ain-date-input [(ngModel)]="postModel.scheduleDateUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.social.posts.fields.scheduleTime')"><input type="time" [(ngModel)]="postModel.scheduleTimeUtc" /></ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="postDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="savePost()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .checkbox-field { display: flex; align-items: center; gap: 0.5rem; }
    .hint { margin: 0; color: #6b7280; font-size: 0.875rem; }
  `]
})
export class GrowthSocialPostsComponent implements OnInit {
  private readonly service = inject(GrowthMarketingService);
  private readonly growthAdmin = inject(GrowthAdminService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(false);
  readonly accounts = signal<GrowthSocialAccount[]>([]);
  readonly posts = signal<GrowthSocialPost[]>([]);
  readonly listening = signal<GrowthSocialListeningItem[]>([]);
  readonly activeConnectors = signal<GrowthConnectorDto[]>([]);
  readonly accountDialogVisible = signal(false);
  readonly editingAccountId = signal<string | null>(null);
  readonly postDialogVisible = signal(false);

  accountModel = {
    connectorKey: null as string | null,
    networkKey: 'telegram',
    displayName: '',
    status: 'Active',
    sandboxMode: true,
    externalAccountRef: '' as string,
    botToken: '' as string,
    hasBotCredential: false
  };
  postModel = {
    contentAssetId: null as string | null,
    socialAccountId: null as string | null,
    networkKey: 'instagram',
    scheduleDateUtc: null as string | null,
    scheduleTimeUtc: '09:00'
  };

  readonly networkOptions = buildGrowthSocialNetworkOptions(key => this.t(key));
  readonly accountStatusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Pending', label: 'Pending' }
  ];

  ngOnInit(): void {
    this.load();
    this.loadActiveConnectors();
  }

  activeConnectorOptions() {
    return this.activeConnectors().map(row => ({
      value: row.connectorKey,
      label: `${row.displayName} (${row.providerKey})`
    }));
  }

  loadActiveConnectors(): void {
    this.growthAdmin.listGrowthConnectors().subscribe({
      next: rows => this.activeConnectors.set(rows.filter(row => row.status === 'Active')),
      error: () => this.activeConnectors.set([])
    });
  }

  socialAccountOptions() {
    return this.accounts().map(row => ({
      value: row.id,
      label: `${row.displayName} (${row.networkKey})`
    }));
  }

  openPostDialog(): void {
    this.postModel = { contentAssetId: null, socialAccountId: null, networkKey: 'instagram', scheduleDateUtc: null, scheduleTimeUtc: '09:00' };
    this.postDialogVisible.set(true);
  }

  openNewAccountDialog(): void {
    this.editingAccountId.set(null);
    this.accountModel = {
      connectorKey: null,
      networkKey: 'telegram',
      displayName: '',
      status: 'Active',
      sandboxMode: true,
      externalAccountRef: '',
      botToken: '',
      hasBotCredential: false
    };
    this.accountDialogVisible.set(true);
  }

  openEditAccountDialog(row: GrowthSocialAccount): void {
    this.editingAccountId.set(row.id);
    this.accountModel = {
      connectorKey: null,
      networkKey: row.networkKey,
      displayName: row.displayName,
      status: row.status,
      sandboxMode: row.sandboxMode,
      externalAccountRef: row.externalAccountRef ?? '',
      botToken: '',
      hasBotCredential: !!row.hasBotCredential
    };
    this.accountDialogVisible.set(true);
  }

  load(): void {
    this.loading.set(true);
    this.service.listSocialAccounts().subscribe({ next: rows => this.accounts.set(rows) });
    this.service.listSocialPosts().subscribe({ next: rows => this.posts.set(rows) });
    this.service.listSocialListening().subscribe({
      next: rows => {
        this.listening.set(rows);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  saveAccount(): void {
    const connector = this.activeConnectors().find(row => row.connectorKey === this.accountModel.connectorKey);
    const networkKey = (this.accountModel.networkKey || connector?.providerKey || 'telegram').trim().toLowerCase();
    const chatId = this.accountModel.externalAccountRef.trim();
    const botToken = this.accountModel.botToken.trim();
    const isMessenger = networkKey === 'telegram' || networkKey === 'bale';
    const editingId = this.editingAccountId();
    const hasCredential = !!botToken || (editingId != null && this.accountModel.hasBotCredential);

    if (isMessenger && botToken && !chatId) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.social.posts.errors.chatIdRequired')
      });
      return;
    }

    if (isMessenger && !this.accountModel.sandboxMode && !chatId) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.social.posts.errors.chatIdRequired')
      });
      return;
    }

    if (isMessenger && !this.accountModel.sandboxMode && !hasCredential) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.social.posts.errors.botTokenRequired')
      });
      return;
    }

    // Bot token forces live mode on the server; keep UI checkbox honest for create.
    const sandboxMode = isMessenger
      ? (botToken ? false : this.accountModel.sandboxMode)
      : true;

    const payload = {
      networkKey,
      displayName: this.accountModel.displayName.trim() || connector?.displayName || networkKey,
      status: this.accountModel.status,
      sandboxMode,
      externalAccountRef: chatId || null,
      botToken: botToken || null
    };

    const request$ = editingId
      ? this.service.updateSocialAccount(editingId, payload)
      : this.service.createSocialAccount(payload);

    request$.subscribe({
      next: () => {
        this.accountDialogVisible.set(false);
        this.editingAccountId.set(null);
        this.accountModel.botToken = '';
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        this.load();
      },
      error: () => {
        this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') });
      }
    });
  }

  savePost(): void {
    if (!this.postModel.contentAssetId)
      return;
    this.service.createSocialPost({
      contentAssetId: this.postModel.contentAssetId,
      socialAccountId: this.postModel.socialAccountId,
      networkKey: this.postModel.networkKey,
      scheduledAtUtc: combineUtcDateAndTime(this.postModel.scheduleDateUtc, this.postModel.scheduleTimeUtc)
    }).subscribe({
      next: () => {
        this.postDialogVisible.set(false);
        this.load();
      }
    });
  }

  approvePost(row: GrowthSocialPost): void {
    this.service.approveSocialPost(row.id).subscribe({ next: () => this.load() });
  }

  schedulePost(row: GrowthSocialPost): void {
    this.service.scheduleSocialPost(row.id, new Date(Date.now() + 3600000).toISOString()).subscribe({ next: () => this.load() });
  }

  publishPost(row: GrowthSocialPost): void {
    this.service.publishSocialPost(row.id).subscribe({ next: () => this.load() });
  }

  ingestListening(): void {
    this.service.ingestSocialListening({
      networkKey: 'instagram',
      externalRef: `listen-${Date.now()}`,
      partyId: null,
      contentAssetId: null,
      payloadText: 'Need demo and pricing details',
      sentimentScore: 0.4,
      intentTagsJson: '["demo","pricing"]',
      status: 'New'
    }).subscribe({ next: () => this.load() });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }
}
